import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence, useMotionValue, useMotionTemplate } from 'framer-motion'
import {
  FONT_DISPLAY, FONT_BODY, FONT_MONO,
  Vortex
} from '../../pages/SummitLandingpage.tsx'

/* ==========================================================================
   1. ServicesHero
   ========================================================================== */
export function ServicesHero() {
  const [isMobile, setIsMobile] = useState(false)
  const sectionRef = useRef<HTMLDivElement>(null)
  const mouseX = useMotionValue(0)
  const mouseY = useMotionValue(0)

  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth < 768)
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  useEffect(() => {
    if (isMobile) return
    const handleMouseMove = (event: MouseEvent) => {
      if (sectionRef.current) {
        const { left, top } = sectionRef.current.getBoundingClientRect()
        mouseX.set(event.clientX - left)
        mouseY.set(event.clientY - top)
      }
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [isMobile, mouseX, mouseY])

  const spotlightMask = useMotionTemplate`radial-gradient(
    320px circle at ${mouseX}px ${mouseY}px,
    black 20%,
    transparent 100%
  )`

  return (
    <section
      ref={sectionRef}
      className="gl-theme-transition"
      style={{
        position: 'relative',
        minHeight: '85svh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '120px 20px 60px',
        background: 'var(--gl-bg)', // Hero -> --gl-bg
        overflow: 'hidden',
      }}
    >
      {/* Background Vortex */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.3,
          pointerEvents: 'none',
        }}
      >
        <Vortex
          backgroundColor="var(--gl-bg)"
          particleCount={80}
          baseSpeed={0.2}
          rangeSpeed={0.8}
          baseRadius={0.6}
          rangeRadius={1.2}
        />
      </div>

      {/* Spotlight Grid Overlay */}
      {!isMobile && (
        <motion.div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.12,
            backgroundImage: `
              linear-gradient(rgba(56, 189, 248, 0.25) 1px, transparent 1px),
              linear-gradient(90deg, rgba(56, 189, 248, 0.25) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
            WebkitMaskImage: spotlightMask,
            maskImage: spotlightMask,
          }}
        />
      )}

      {/* Content Container */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: 960,
          width: '100%',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 24,
        }}
      >
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <div style={{ width: 24, height: 1, background: 'var(--gl-accent-ice)' }} />
          <span
            style={{
              fontFamily: FONT_MONO,
              fontSize: 10,
              letterSpacing: '0.24em',
              textTransform: 'uppercase',
              color: 'var(--gl-accent-bright)', // Label -> #7DD3FC
            }}
          >
            COGNITIVE ENGINEERING · AI SERVICES
          </span>
          <div style={{ width: 24, height: 1, background: 'var(--gl-accent-ice)' }} />
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          style={{
            margin: 0,
            fontFamily: FONT_DISPLAY,
            fontSize: 'clamp(48px, 6vw, 92px)',
            lineHeight: 0.95,
            letterSpacing: '0.04em',
            color: 'var(--gl-text)', // Heading -> #F8FAFC
            textTransform: 'uppercase',
          }}
        >
          AI SYSTEMS BUILT FOR REAL-WORLD OPERATIONS
        </motion.h1>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          style={{
            margin: '8px 0 16px',
            fontFamily: FONT_BODY,
            fontSize: 'clamp(15px, 2vw, 18px)',
            lineHeight: 1.65,
            color: 'var(--gl-text-muted)', // Description -> #94A3B8
            maxWidth: 720,
          }}
        >
          From strategy and AI engineering to intelligent automation and production deployment, GLIEDAI helps organizations turn complex workflows into practical AI-powered systems.
        </motion.p>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          style={{
            display: 'flex',
            gap: 16,
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {/* Primary CTA */}
          <button
            type="button"
            className="gl-theme-transition"
            onClick={() => document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth' })}
            style={{
              padding: '14px 28px',
              background: '#F8FAFC', // Dark Primary CTA -> #F8FAFC
              color: '#070B10', // Dark Primary Text -> #070B10
              border: 'none',
              fontFamily: FONT_MONO,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              clipPath: 'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)',
              transition: 'transform 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.03)')}
            onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
          >
            START AN AI PROJECT
          </button>
          {/* Secondary CTA */}
          <button
            type="button"
            className="gl-theme-transition"
            onClick={() => document.getElementById('categories')?.scrollIntoView({ behavior: 'smooth' })}
            style={{
              padding: '14px 28px',
              background: 'transparent',
              color: '#F8FAFC', // Dark Secondary CTA text -> #F8FAFC
              border: '1px solid rgba(255,255,255,0.14)', // Dark Secondary border -> rgba(255,255,255,0.14)
              fontFamily: FONT_MONO,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              clipPath: 'polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)',
              transition: 'background 0.2s, border-color 0.2s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.05)'
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.14)'
            }}
          >
            EXPLORE SERVICES
          </button>
        </motion.div>
      </div>

      {/* Decorative scroll indicator */}
      <div
        style={{
          position: 'absolute',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          opacity: 0.4,
        }}
      >
        <span style={{ fontFamily: FONT_MONO, fontSize: 8, letterSpacing: '0.15em', color: 'var(--gl-text-muted)' }}>SCROLL</span>
        <div style={{ width: 1, height: 32, background: 'linear-gradient(to bottom, var(--gl-text-muted), transparent)' }} />
      </div>
    </section>
  )
}

/* ==========================================================================
   2. ServiceCategories
   ========================================================================== */
const CATEGORIES = [
  {
    icon: 'analytics',
    title: 'AI Strategy & Advisory',
    sub: 'AI DISCOVERY · ROADMAP · ARCHITECTURE',
    desc: 'Identify high-value AI opportunities, define the right technical approach, and create a practical roadmap for moving from concept to production.',
    includes: [
      'AI use-case discovery',
      'AI readiness assessment',
      'Solution architecture',
      'Implementation roadmap',
      'AI adoption planning'
    ]
  },
  {
    icon: 'psychology',
    title: 'Generative AI & LLM Solutions',
    sub: 'LLMs · COPILOTS · CUSTOM AI APPLICATIONS',
    desc: 'Build tailored AI applications that support reasoning, content generation, knowledge access, and domain-specific business workflows.',
    includes: [
      'LLM applications',
      'AI copilots',
      'Prompt & model workflows',
      'Domain adaptation',
      'Generative AI interfaces'
    ]
  },
  {
    icon: 'manage_search',
    title: 'Knowledge & RAG Systems',
    sub: 'RAG · DOCUMENT INTELLIGENCE · SEMANTIC SEARCH',
    desc: 'Connect AI to trusted organizational knowledge so teams can find information, understand documents, and receive context-aware responses.',
    includes: [
      'Document processing',
      'Knowledge extraction',
      'Semantic search',
      'RAG pipelines',
      'Knowledge assistants'
    ]
  },
  {
    icon: 'smart_toy',
    title: 'AI Agents & Workflow Automation',
    sub: 'AI AGENTS · ORCHESTRATION · AUTOMATION',
    desc: 'Design intelligent agents that use tools, coordinate multi-step workflows, make informed decisions, and automate repetitive operational tasks.',
    includes: [
      'AI agents',
      'Tool calling',
      'Workflow orchestration',
      'Decision automation',
      'Multi-agent workflows'
    ]
  },
  {
    icon: 'record_voice_over',
    title: 'Voice AI & Conversational Systems',
    sub: 'ASR · TTS · VOICE AGENTS · CALL BOTS',
    desc: 'Build real-time voice experiences that understand users, respond naturally, and connect conversations with business workflows.',
    includes: [
      'Speech recognition',
      'Text-to-speech',
      'Voice assistants',
      'AI call bots',
      'Voice workflow integration'
    ]
  },
  {
    icon: 'translate',
    title: 'Translation & Language Intelligence',
    sub: 'MULTILINGUAL AI · TRANSLATION · LOCALIZATION',
    desc: 'Develop language systems for multilingual conversations, documents, applications, and regional-language experiences.',
    includes: [
      'Machine translation',
      'Speech translation',
      'Regional languages',
      'Domain terminology',
      'Language localization'
    ]
  },
  {
    icon: 'hub',
    title: 'AI Integration & Engineering',
    sub: 'APIs · DATA · ENTERPRISE SYSTEMS · DEPLOYMENT',
    desc: 'Connect AI capabilities with existing applications, databases, communication platforms, and operational systems.',
    includes: [
      'API integration',
      'Enterprise system integration',
      'Data pipelines',
      'AI infrastructure',
      'Production deployment'
    ]
  },
  {
    icon: 'monitoring',
    title: 'AI Optimization & Managed Support',
    sub: 'EVALUATION · MONITORING · PERFORMANCE · IMPROVEMENT',
    desc: 'Continuously evaluate deployed AI systems, improve reliability and quality, and support long-term production operations.',
    includes: [
      'AI evaluation',
      'Quality monitoring',
      'Performance optimization',
      'System maintenance',
      'Continuous improvement'
    ]
  }
]

export function ServiceCategories() {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)

  return (
    <section
      id="categories"
      className="gl-theme-transition"
      style={{
        position: 'relative',
        padding: '100px 20px',
        background: 'var(--gl-bg)', // Core Services -> --gl-bg
        overflow: 'hidden',
      }}
    >
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {/* Section Heading */}
        <div style={{ marginBottom: 60, maxWidth: 640 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 30, height: 1, background: 'var(--gl-accent-ice)' }} />
            <span style={{ fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'var(--gl-accent-bright)' }}>
              Core Capabilities
            </span>
          </div>
          <h2
            style={{
              margin: 0,
              fontFamily: FONT_DISPLAY,
              fontSize: 'clamp(36px, 5vw, 68px)',
              lineHeight: 0.95,
              letterSpacing: '0.03em',
              color: 'var(--gl-text)',
              textTransform: 'uppercase',
            }}
          >
            Core AI Services
          </h2>
        </div>

        {/* Categories Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 24,
          }}
        >
          {CATEGORIES.map((item, index) => {
            const isHovered = hoveredIdx === index
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
                onMouseEnter={() => setHoveredIdx(index)}
                onMouseLeave={() => setHoveredIdx(null)}
                className="gl-theme-transition"
                style={{
                  position: 'relative',
                  padding: 34,
                  background: isHovered ? 'var(--gl-surface-hover)' : 'var(--gl-bg-elevated)', // Dark card style base vs hover
                  border: '1px solid',
                  borderColor: isHovered ? 'var(--gl-accent)' : 'var(--gl-border)', // Active / normal border
                  cursor: 'pointer',
                  overflow: 'hidden',
                  clipPath: 'polygon(12px 0%, 100% 0%, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0% 100%, 0% 12px)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Spotlight Background Glow */}
                <motion.div
                  animate={{
                    opacity: isHovered ? 1 : 0,
                  }}
                  transition={{ duration: 0.3 }}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'radial-gradient(circle 220px at center, rgba(56, 189, 248, 0.055) 0%, transparent 80%)',
                    pointerEvents: 'none',
                    zIndex: 0,
                  }}
                />

                {/* Content */}
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', flexGrow: 1 }}>
                  {/* Icon */}
                  <div style={{ marginBottom: 20 }}>
                    <span 
                      className="material-symbols-outlined service-icon"
                      style={{
                        color: isHovered ? 'var(--gl-accent)' : 'var(--gl-accent-bright)', // Active -> #38BDF8, Normal Label -> #7DD3FC
                        transform: isHovered ? 'translateY(-2px) scale(1.05)' : 'none',
                        display: 'inline-block'
                      }}
                    >
                      {item.icon}
                    </span>
                  </div>

                  {/* Header */}
                  <div
                    style={{
                      fontFamily: FONT_MONO,
                      fontSize: 8,
                      letterSpacing: '0.15em',
                      color: 'var(--gl-accent-bright)', // Subheading labels -> #7DD3FC
                      textTransform: 'uppercase',
                      marginBottom: 8,
                    }}
                  >
                    {item.sub}
                  </div>

                  <h3
                    style={{
                      margin: '0 0 14px',
                      fontFamily: FONT_DISPLAY,
                      fontSize: 22,
                      letterSpacing: '0.04em',
                      color: 'var(--gl-text)', // Heading -> #F8FAFC
                      textTransform: 'uppercase',
                    }}
                  >
                    {item.title}
                  </h3>

                  <p
                    style={{
                      margin: '0 0 24px',
                      fontFamily: FONT_BODY,
                      fontSize: 13,
                      lineHeight: 1.6,
                      color: 'var(--gl-text-muted)', // Description -> #94A3B8
                    }}
                  >
                    {item.desc}
                  </p>

                  {/* Checkbox Checklist */}
                  <div
                    style={{
                      marginTop: 'auto',
                      borderTop: '1px solid var(--gl-border)',
                      paddingTop: 20,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: FONT_MONO,
                        fontSize: 8,
                        color: isHovered ? 'var(--gl-accent)' : 'var(--gl-text-muted)',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        marginBottom: 10,
                      }}
                    >
                      Includes
                    </div>
                    <ul
                      style={{
                        margin: 0,
                        padding: 0,
                        listStyle: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                      }}
                    >
                      {item.includes.map(inc => (
                        <li
                          key={inc}
                          style={{
                            fontFamily: FONT_BODY,
                            fontSize: 12,
                            color: 'var(--gl-text-muted)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <span className="material-symbols-outlined service-bullet" style={{ color: 'var(--gl-accent)' }}>
                            bolt
                          </span>
                          {inc}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Left glow line */}
                <motion.div
                  animate={{
                    height: isHovered ? '100%' : '0%',
                  }}
                  transition={{ duration: 0.3 }}
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: 2,
                    background: 'var(--gl-accent)',
                  }}
                />
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

/* ==========================================================================
   3. WhatWeBuild
   ========================================================================== */
const SYSTEMS = [
  {
    id: 'voice',
    title: 'Voice & Conversational Systems',
    desc: 'Production-ready voice systems for assistants, customer interactions, call automation, and multilingual communication.',
    features: [
      'VOICE AGENTS',
      'AI CALL BOTS',
      'TRANSLATION SYSTEMS',
    ],
    architecture: 'WebRTC Speech Pipeline -> Orchestration Model -> Latency-Optimized Speech Synthesizer',
    icon: 'record_voice_over',
  },
  {
    id: 'knowledge',
    title: 'Knowledge & AI Search',
    desc: 'Intelligent knowledge systems that process enterprise information and deliver grounded answers through search and RAG.',
    features: [
      'KNOWLEDGE ASSISTANTS',
      'RAG SYSTEMS',
      'DOCUMENT INTELLIGENCE',
    ],
    architecture: 'Document Parser -> Metadata Extractor -> Vector Embedding & Qdrant Index -> Hybrid Search RAG',
    icon: 'manage_search',
  },
  {
    id: 'agents',
    title: 'AI Agents & Automation',
    desc: 'Autonomous workflows that connect AI reasoning with tools, systems, and operational actions.',
    features: [
      'WORKFLOW AGENTS',
      'AI COPILOTS',
      'ENTERPRISE AI INTEGRATIONS',
    ],
    architecture: 'Goal Planner -> Tool Interface -> State Engine -> Enterprise API Integration Rail',
    icon: 'smart_toy',
  },
  {
    id: 'language',
    title: 'Language Systems',
    desc: 'Translation and multilingual AI solutions for voice, documents, applications, and regional-language communication.',
    features: [
      'TRANSLATION SYSTEMS',
      'DOCUMENT INTELLIGENCE',
      'AI COPILOTS',
    ],
    architecture: 'Context-Aware Translation Models -> Dialect Adapter -> Regional Terminology Database',
    icon: 'translate',
  },
]

export function WhatWeBuild() {
  const [activeTab, setActiveTab] = useState('voice')
  const currentSystem = SYSTEMS.find(s => s.id === activeTab) || SYSTEMS[0]

  return (
    <section
      id="what-we-build"
      className="gl-theme-transition"
      style={{
        position: 'relative',
        padding: '100px 20px',
        background: 'var(--gl-bg-soft)', // What We Build -> --gl-bg-soft
        overflow: 'hidden',
      }}
    >
      {/* Background design accents */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '40vw',
          height: '40vw',
          background: 'radial-gradient(circle, rgba(58,100,120,0.03) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 60 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 30, height: 1, background: 'var(--gl-accent-ice)' }} />
            <span style={{ fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'var(--gl-accent-bright)' }}>
              Custom Deliverables
            </span>
          </div>
          <h2
            style={{
              margin: 0,
              fontFamily: FONT_DISPLAY,
              fontSize: 'clamp(36px, 5vw, 68px)',
              lineHeight: 0.95,
              letterSpacing: '0.03em',
              color: 'var(--gl-text)',
              textTransform: 'uppercase',
            }}
          >
            What We Build
          </h2>
        </div>

        {/* Tab Layout */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 40,
            alignItems: 'start',
          }}
        >
          {/* Left: Tab Selectors */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {SYSTEMS.map(sys => {
              const isActive = sys.id === activeTab
              return (
                <button
                  key={sys.id}
                  type="button"
                  className="gl-theme-transition"
                  onClick={() => setActiveTab(sys.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 20,
                    padding: '24px 28px',
                    background: isActive ? 'var(--gl-surface-hover)' : 'transparent',
                    border: '1px solid',
                    borderColor: isActive ? 'var(--gl-accent)' : 'var(--gl-border)', // Active -> --gl-accent, Normal -> --gl-border
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.3s ease',
                    clipPath: 'polygon(8px 0%, 100% 0%, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0% 100%, 0% 8px)',
                    outline: 'none',
                  }}
                >
                  <span 
                    className="material-symbols-outlined"
                    style={{ 
                      fontSize: 24, 
                      color: isActive ? 'var(--gl-accent)' : 'var(--gl-accent-bright)',
                      opacity: isActive ? 1 : 0.5 
                    }}
                  >
                    {sys.icon}
                  </span>
                  <div style={{ flexGrow: 1 }}>
                    <h3
                      style={{
                        margin: 0,
                        fontFamily: FONT_DISPLAY,
                        fontSize: 20,
                        letterSpacing: '0.04em',
                        color: isActive ? 'var(--gl-text)' : 'var(--gl-text-muted)',
                        textTransform: 'uppercase',
                        transition: 'color 0.3s',
                      }}
                    >
                      {sys.title}
                    </h3>
                  </div>
                  {isActive && (
                    <motion.span
                      layoutId="active-bullet"
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: 'var(--gl-accent)',
                        boxShadow: 'var(--gl-glow)',
                      }}
                    />
                  )}
                </button>
              )
            })}
          </div>

          {/* Right: Tab Detail Panel */}
          <div
            className="gl-theme-transition"
            style={{
              padding: 'clamp(24px, 4vw, 44px)',
              background: 'var(--gl-bg-elevated)', // Dark card style base
              border: '1px solid var(--gl-border)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              clipPath: 'polygon(16px 0%, 100% 0%, 100% calc(100% - 16px), calc(100% - 16px) 100%, 0% 100%, 0% 16px)',
              minHeight: 380,
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSystem.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.35 }}
              >
                <span
                  style={{
                    fontFamily: FONT_MONO,
                    fontSize: 8,
                    color: 'var(--gl-accent-bright)', // Subheading -> #7DD3FC
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase',
                    display: 'block',
                    marginBottom: 8,
                  }}
                >
                  SYSTEM BLUEPRINT
                </span>

                <h3
                  style={{
                    margin: '0 0 16px',
                    fontFamily: FONT_DISPLAY,
                    fontSize: 'clamp(28px, 4vw, 38px)',
                    color: 'var(--gl-text)', // Heading -> #F8FAFC
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  {currentSystem.title}
                </h3>

                <p
                  style={{
                    margin: '0 0 28px',
                    fontFamily: FONT_BODY,
                    fontSize: 14,
                    lineHeight: 1.65,
                    color: 'var(--gl-text-muted)', // Description -> #94A3B8
                  }}
                >
                  {currentSystem.desc}
                </p>

                <h4
                  style={{
                    margin: '0 0 12px',
                    fontFamily: FONT_MONO,
                    fontSize: 10,
                    color: 'var(--gl-text-secondary)', // Subheading -> #DBE4EE
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                  }}
                >
                  What the client receives:
                </h4>

                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: '0 0 32px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  {currentSystem.features.map(f => (
                    <li
                      key={f}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        fontFamily: FONT_BODY,
                        fontSize: 13,
                        color: 'var(--gl-text-muted)',
                      }}
                    >
                      <span 
                        className="material-symbols-outlined" 
                        style={{ fontSize: 14, color: 'var(--gl-accent)', verticalAlign: 'middle' }}
                      >
                        done
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                <div
                  style={{
                    borderTop: '1px solid var(--gl-border)',
                    paddingTop: 20,
                  }}
                >
                  <span
                    style={{
                      fontFamily: FONT_MONO,
                      fontSize: 8,
                      color: 'var(--gl-text-muted)',
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      display: 'block',
                      marginBottom: 6,
                    }}
                  >
                    ARCHITECTURE STACK
                  </span>
                  <div
                    style={{
                      fontFamily: FONT_MONO,
                      fontSize: 11,
                      color: 'var(--gl-accent-bright)', // Highlight -> #7DD3FC
                      lineHeight: 1.4,
                    }}
                  >
                    {currentSystem.architecture}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ==========================================================================
   4. DeliveryProcess
   ========================================================================== */
const STEPS = [
  {
    num: '01',
    title: 'DISCOVER',
    desc: 'Understand the business challenge, workflow, users, data, and success criteria.',
  },
  {
    num: '02',
    title: 'ARCHITECT',
    desc: 'Design the AI architecture, integrations, security boundaries, and deployment approach.',
  },
  {
    num: '03',
    title: 'ENGINEER',
    desc: 'Build the models, agents, RAG pipelines, voice systems, interfaces, and workflows.',
  },
  {
    num: '04',
    title: 'INTEGRATE',
    desc: 'Connect AI with existing applications, databases, APIs, communication channels, and operational systems.',
  },
  {
    num: '05',
    title: 'DEPLOY',
    desc: 'Move the validated solution into its target production environment.',
  },
  {
    num: '06',
    title: 'OPTIMIZE',
    desc: 'Evaluate performance, improve quality, and continuously evolve the system.',
  },
]

export function DeliveryProcess() {
  const [activeStep, setActiveStep] = useState(0)

  return (
    <section
      id="delivery-process"
      className="gl-theme-transition"
      style={{
        position: 'relative',
        padding: '100px 20px',
        background: 'var(--gl-bg)', // How We Deliver -> --gl-bg
        overflow: 'hidden',
      }}
    >
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 60, textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 24, height: 1, background: 'var(--gl-accent-ice)' }} />
            <span style={{ fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'var(--gl-accent-bright)' }}>
              ENGINEERING METHODOLOGY
            </span>
            <div style={{ width: 24, height: 1, background: 'var(--gl-accent-ice)' }} />
          </div>
          <h2
            style={{
              margin: 0,
              fontFamily: FONT_DISPLAY,
              fontSize: 'clamp(36px, 5vw, 68px)',
              lineHeight: 0.95,
              letterSpacing: '0.03em',
              color: 'var(--gl-text)',
              textTransform: 'uppercase',
            }}
          >
            How We Deliver
          </h2>
        </div>

        {/* Process Roadmap */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 40,
            paddingLeft: 'clamp(20px, 6vw, 60px)',
          }}
        >
          {/* Vertical progress rail */}
          <div
            style={{
              position: 'absolute',
              left: 4,
              top: 10,
              bottom: 10,
              width: 1,
              background: 'var(--gl-border)',
            }}
          />

          {/* Active progress rail overlay */}
          <motion.div
            style={{
              position: 'absolute',
              left: 4,
              top: 10,
              width: 1,
              background: 'var(--gl-accent)',
              height: `${(activeStep / (STEPS.length - 1)) * 100}%`,
              transformOrigin: 'top',
              boxShadow: 'var(--gl-glow)',
            }}
            transition={{ type: 'spring', stiffness: 80, damping: 20 }}
          />

          {STEPS.map((step, idx) => {
            const isActive = activeStep >= idx
            return (
              <div
                key={step.num}
                onMouseEnter={() => setActiveStep(idx)}
                style={{
                  position: 'relative',
                  cursor: 'pointer',
                }}
              >
                {/* Dot */}
                <motion.div
                  animate={{
                    borderColor: isActive ? 'var(--gl-accent)' : 'var(--gl-border)',
                    backgroundColor: isActive ? 'var(--gl-bg-elevated)' : 'var(--gl-bg-soft)',
                    scale: isActive ? 1.15 : 1,
                  }}
                  style={{
                    position: 'absolute',
                    left: 'calc(-1 * clamp(20px, 6vw, 60px) - 6px)',
                    top: 6,
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    border: '2px solid',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 5,
                    background: 'var(--gl-bg-elevated)',
                  }}
                >
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: isActive ? 'var(--gl-accent)' : 'transparent',
                    }}
                  />
                </motion.div>

                {/* Step content */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.6, delay: idx * 0.08 }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    padding: '16px 20px',
                    background: isActive ? 'var(--gl-bg-elevated)' : 'transparent', // Contrast layer for step cards
                    border: '1px solid',
                    borderColor: isActive ? 'var(--gl-border)' : 'transparent',
                    borderRadius: 4,
                    transition: 'all 0.3s ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
                    <span
                      style={{
                        fontFamily: FONT_MONO,
                        fontSize: 12,
                        color: isActive ? 'var(--gl-accent)' : 'var(--gl-text-muted)',
                        fontWeight: 700,
                      }}
                    >
                      {step.num}
                    </span>
                    <h3
                      style={{
                        margin: 0,
                        fontFamily: FONT_DISPLAY,
                        fontSize: 24,
                        letterSpacing: '0.04em',
                        color: isActive ? 'var(--gl-text)' : 'var(--gl-text-muted)',
                        textTransform: 'uppercase',
                        transition: 'color 0.3s',
                      }}
                    >
                      {step.title}
                    </h3>
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontFamily: FONT_BODY,
                      fontSize: 13,
                      lineHeight: 1.6,
                      color: isActive ? 'var(--gl-text-secondary)' : 'var(--gl-text-muted)',
                      maxWidth: 720,
                      transition: 'color 0.3s',
                    }}
                  >
                    {step.desc}
                  </p>
                </motion.div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

/* ==========================================================================
   5. AITechnology
   ========================================================================== */
const LAYERS = [
  {
    title: 'VOICE',
    sub: 'ASR · TTS · Voice AI · Call Bots',
    icon: 'record_voice_over',
    desc: 'Low-latency speech recognition, neural voice synthesis, and direct SIP/WebRTC telephony integration.',
  },
  {
    title: 'LANGUAGE',
    sub: 'LLMs · NLP · Translation · Localization',
    icon: 'translate',
    desc: 'Fine-tuned LLMs, contextual dialog engines, multilingual translation rails, and local dialect adaptation.',
  },
  {
    title: 'KNOWLEDGE',
    sub: 'RAG · Semantic Search · Document Intelligence',
    icon: 'manage_search',
    desc: 'Hybrid vector search, custom chunking, RAG pipelines, and automated metadata extraction.',
  },
  {
    title: 'AGENTS',
    sub: 'Tool Calling · Memory · Orchestration · Automation',
    icon: 'smart_toy',
    desc: 'Goal-oriented planning, multi-agent coordination, persistent memory, and autonomous task execution.',
  },
  {
    title: 'INTEGRATION',
    sub: 'APIs · Data · Enterprise Systems',
    icon: 'hub',
    desc: 'Secure API orchestration, data pipeline syndication, and deep connection to core business systems.',
  },
  {
    title: 'SECURITY',
    sub: 'Access Control · Data Isolation · Governance',
    icon: 'security',
    desc: 'Strict data tenant isolation, role-based access controls, automated compliance logging, and zero-leak training.',
  },
]

export function AITechnology() {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)

  return (
    <section
      id="technology-stack"
      className="gl-theme-transition"
      style={{
        position: 'relative',
        padding: '100px 20px',
        background: 'var(--gl-bg-soft)', // Architecture -> --gl-bg-soft
        overflow: 'hidden',
      }}
    >
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 60, maxWidth: 640 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 30, height: 1, background: 'var(--gl-accent-ice)' }} />
            <span style={{ fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'var(--gl-accent-bright)' }}>
              SYSTEM ARCHITECTURE
            </span>
          </div>
          <h2
            style={{
              margin: 0,
              fontFamily: FONT_DISPLAY,
              fontSize: 'clamp(36px, 5vw, 68px)',
              lineHeight: 0.95,
              letterSpacing: '0.03em',
              color: 'var(--gl-text)',
              textTransform: 'uppercase',
            }}
          >
            AI Technology Stack
          </h2>
        </div>

        {/* Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 24,
          }}
        >
          {LAYERS.map((layer, idx) => {
            const isHovered = hoveredIdx === idx
            return (
              <div
                key={layer.title}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
                className="gl-theme-transition"
                style={{
                  background: isHovered ? 'var(--gl-surface-hover)' : 'var(--gl-bg-elevated)', // Dark card base vs hover
                  border: '1px solid',
                  borderColor: isHovered ? 'var(--gl-accent)' : 'var(--gl-border)', // Hover/Active border
                  cursor: 'pointer',
                  overflow: 'hidden',
                  clipPath: 'polygon(12px 0%, 100% 0%, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0% 100%, 0% 12px)',
                  position: 'relative',
                  padding: 34,
                }}
              >
                {/* Spotlight Background Glow */}
                <motion.div
                  animate={{ opacity: isHovered ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'radial-gradient(circle 180px at center, rgba(56, 189, 248, 0.05) 0%, transparent 85%)',
                    pointerEvents: 'none',
                    zIndex: 0,
                  }}
                />

                {/* Content */}
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                    <h3
                      style={{
                        margin: 0,
                        fontFamily: FONT_DISPLAY,
                        fontSize: 28,
                        letterSpacing: '0.05em',
                        color: 'var(--gl-text)',
                      }}
                    >
                      {layer.title}
                    </h3>
                    <span 
                      className="material-symbols-outlined service-icon"
                      style={{
                        color: isHovered ? 'var(--gl-accent)' : 'var(--gl-accent-bright)',
                        transform: isHovered ? 'translateY(-2px) scale(1.05)' : 'none',
                        display: 'inline-block'
                      }}
                    >
                      {layer.icon}
                    </span>
                  </div>

                  <div
                    style={{
                      fontFamily: FONT_MONO,
                      fontSize: 9,
                      letterSpacing: '0.12em',
                      color: isHovered ? 'var(--gl-accent)' : 'var(--gl-accent-bright)',
                      textTransform: 'uppercase',
                      marginBottom: 12,
                      transition: 'color 0.3s',
                    }}
                  >
                    {layer.sub}
                  </div>

                  <p
                    style={{
                      margin: 0,
                      fontFamily: FONT_BODY,
                      fontSize: 13,
                      lineHeight: 1.6,
                      color: 'var(--gl-text-muted)',
                    }}
                  >
                    {layer.desc}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

/* ==========================================================================
   6. IndustrySolutions
   ========================================================================== */
const INDUSTRIES = [
  {
    title: 'Agriculture',
    sub: 'AGRICULTURAL INTELLIGENCE · VOICE · WEATHER',
    icon: 'agriculture',
    solutions: [
      'Weather intelligence',
      'Crop advisory',
      'Farmer voice assistance',
      'Regional-language support',
      'Pest & disease intelligence'
    ]
  },
  {
    title: 'Healthcare',
    sub: 'PATIENT EXPERIENCE · DOCUMENTATION · ASSISTANCE',
    icon: 'local_hospital',
    solutions: [
      'Patient voice assistants',
      'Appointment automation',
      'Medical documentation',
      'Multilingual communication',
      'Healthcare knowledge systems'
    ]
  },
  {
    title: 'Banking & Finance',
    sub: 'CUSTOMER SERVICE · DOCUMENT INTELLIGENCE · AUTOMATION',
    icon: 'account_balance',
    solutions: [
      'Customer voice automation',
      'KYC & document intelligence',
      'Compliance workflows',
      'Financial knowledge assistants',
      'Operational automation'
    ]
  },
  {
    title: 'Logistics',
    sub: 'OPERATIONS · ROUTES · COMMUNICATION',
    icon: 'local_shipping',
    solutions: [
      'Shipment intelligence',
      'Route & delay insights',
      'Customer notifications',
      'Voice-enabled operations',
      'Workflow automation'
    ]
  },
  {
    title: 'Education',
    sub: 'LEARNING · ASSISTANCE · LANGUAGE',
    icon: 'school',
    solutions: [
      'AI tutors',
      'Voice learning assistants',
      'Multilingual learning',
      'Knowledge assistants',
      'Assessment automation'
    ]
  },
  {
    title: 'Government',
    sub: 'CITIZEN SERVICES · DOCUMENTS · DIGITAL OPERATIONS',
    icon: 'account_balance',
    solutions: [
      'Citizen service assistants',
      'Document digitization',
      'Multilingual public support',
      'Policy / scheme knowledge systems',
      'Workflow automation'
    ]
  }
]

export function IndustrySolutions() {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)

  return (
    <section
      id="industry-solutions"
      className="gl-theme-transition"
      style={{
        position: 'relative',
        padding: '100px 20px',
        background: 'var(--gl-bg)', // Industries -> --gl-bg
        overflow: 'hidden',
      }}
    >
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 60, maxWidth: 640 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 30, height: 1, background: 'var(--gl-accent-ice)' }} />
            <span style={{ fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'var(--gl-accent-bright)' }}>
              Sector Applications
            </span>
          </div>
          <h2
            style={{
              margin: 0,
              fontFamily: FONT_DISPLAY,
              fontSize: 'clamp(36px, 5vw, 68px)',
              lineHeight: 0.95,
              letterSpacing: '0.03em',
              color: 'var(--gl-text)',
              textTransform: 'uppercase',
            }}
          >
            AI SOLUTIONS FOR EVERY INDUSTRY
          </h2>
        </div>

        {/* Industries Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
            gap: 24,
          }}
        >
          {INDUSTRIES.map((ind, idx) => {
            const isHovered = hoveredIdx === idx
            return (
              <motion.div
                key={ind.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, delay: idx * 0.08, ease: [0.16, 1, 0.3, 1] }}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
                className="gl-theme-transition"
                style={{
                  position: 'relative',
                  padding: 34,
                  background: isHovered ? 'var(--gl-surface-hover)' : 'var(--gl-bg-elevated)', // Dark card base vs hover
                  border: '1px solid',
                  borderColor: isHovered ? 'var(--gl-accent)' : 'var(--gl-border)', // Active / normal border
                  cursor: 'pointer',
                  overflow: 'hidden',
                  clipPath: 'polygon(12px 0%, 100% 0%, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0% 100%, 0% 12px)',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Spotlight Background Glow */}
                <motion.div
                  animate={{
                    opacity: isHovered ? 1 : 0,
                  }}
                  transition={{ duration: 0.3 }}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'radial-gradient(circle 220px at center, rgba(56, 189, 248, 0.055) 0%, transparent 80%)',
                    pointerEvents: 'none',
                    zIndex: 0,
                  }}
                />

                {/* Content */}
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', flexGrow: 1 }}>
                  {/* Icon & Title */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                    <h3
                      style={{
                        margin: 0,
                        fontFamily: FONT_DISPLAY,
                        fontSize: 26,
                        letterSpacing: '0.04em',
                        color: 'var(--gl-text)',
                        textTransform: 'uppercase',
                      }}
                    >
                      {ind.title}
                    </h3>
                    <span 
                      className="material-symbols-outlined service-icon"
                      style={{
                        color: isHovered ? 'var(--gl-accent)' : 'var(--gl-accent-bright)',
                        transform: isHovered ? 'translateY(-2px) scale(1.05)' : 'none',
                        display: 'inline-block'
                      }}
                    >
                      {ind.icon}
                    </span>
                  </div>

                  {/* Eyebrow / Sub */}
                  <div
                    style={{
                      fontFamily: FONT_MONO,
                      fontSize: 8,
                      letterSpacing: '0.15em',
                      color: 'var(--gl-accent-bright)',
                      textTransform: 'uppercase',
                      marginBottom: 20,
                      borderBottom: '1px solid var(--gl-border)',
                      paddingBottom: 12,
                    }}
                  >
                    {ind.sub}
                  </div>

                  {/* Solutions list */}
                  <ul
                    style={{
                      margin: 0,
                      padding: 0,
                      listStyle: 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    {ind.solutions.map(sol => (
                      <li
                        key={sol}
                        style={{
                          fontFamily: FONT_BODY,
                          fontSize: 13,
                          color: 'var(--gl-text-muted)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                        }}
                      >
                        <span className="material-symbols-outlined service-bullet" style={{ color: 'var(--gl-accent)' }}>
                          bolt
                        </span>
                        {sol}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Left glow line */}
                <motion.div
                  animate={{
                    height: isHovered ? '100%' : '0%',
                  }}
                  transition={{ duration: 0.3 }}
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: 2,
                    background: 'var(--gl-accent)',
                  }}
                />
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

/* ==========================================================================
   7. DeploymentSection
   ========================================================================== */
const MODELS = [
  {
    id: 'vpc',
    name: 'Secure Cloud',
    desc: 'Controlled cloud deployment for scalable AI workloads and enterprise integrations.',
    features: ['Isolated Database Stores', 'Direct VPC Peering Integration', 'Automatic Scalability Limits', 'Encrypted TLS 1.3 Communication'],
    badge: 'RECOMMENDED',
  },
  {
    id: 'onprem',
    name: 'Private / On-Premise',
    desc: 'AI systems deployed within customer-managed infrastructure for environments requiring greater control over data and network access.',
    features: ['100% Offline Capability', 'Zero Inbound Network Exposure', 'Direct Hardware Acceleration', 'Custom Firewall Alignment'],
    badge: 'AIR-GAPPED READY',
  },
  {
    id: 'hybrid',
    name: 'Hybrid',
    desc: 'Combine cloud-based intelligence with local systems and data to balance scalability, control, and integration requirements.',
    features: ['Dynamic Fallback Paths', 'Distributed Vector Databases', 'Encrypted TLS 1.3 Communication', 'Localized Data Sanitization'],
    badge: 'FLEXIBLE ENTERPRISE',
  },
]

export function DeploymentSection() {
  const [selectedModel, setSelectedModel] = useState('vpc')

  return (
    <section
      id="deployment"
      className="gl-theme-transition"
      style={{
        position: 'relative',
        padding: '100px 20px',
        background: 'var(--gl-bg-soft)', // Deployment -> --gl-bg-soft
        overflow: 'hidden',
      }}
    >
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 60, maxWidth: 640 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 30, height: 1, background: 'var(--gl-accent-ice)' }} />
            <span style={{ fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'var(--gl-accent-bright)' }}>
              DEPLOYMENT MODEL
            </span>
          </div>
          <h2
            style={{
              margin: 0,
              fontFamily: FONT_DISPLAY,
              fontSize: 'clamp(36px, 5vw, 68px)',
              lineHeight: 0.95,
              letterSpacing: '0.03em',
              color: 'var(--gl-text)',
              textTransform: 'uppercase',
            }}
          >
            Flexible Deployment
          </h2>
        </div>

        {/* Layout */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 24,
          }}
        >
          {MODELS.map(model => {
            const isSelected = selectedModel === model.id
            return (
              <motion.div
                key={model.id}
                onClick={() => setSelectedModel(model.id)}
                whileHover={{ scale: 1.015 }}
                transition={{ duration: 0.25 }}
                className="gl-theme-transition"
                style={{
                  position: 'relative',
                  padding: 34,
                  background: isSelected ? 'var(--gl-surface-hover)' : 'var(--gl-bg-elevated)', // Dark card base vs selected
                  border: '1px solid',
                  borderColor: isSelected ? 'var(--gl-accent)' : 'var(--gl-border)', // Selected border -> --gl-accent
                  cursor: 'pointer',
                  clipPath: 'polygon(12px 0%, 100% 0%, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0% 100%, 0% 12px)',
                }}
              >
                {/* Badge */}
                <div
                  className="gl-theme-transition"
                  style={{
                    position: 'absolute',
                    top: 24,
                    right: 24,
                    fontFamily: FONT_MONO,
                    fontSize: 7,
                    fontWeight: 700,
                    letterSpacing: '0.15em',
                    padding: '4px 10px',
                    background: isSelected ? 'var(--gl-accent-bright)' : 'var(--gl-border)',
                    color: isSelected ? 'var(--gl-bg)' : 'var(--gl-text-muted)',
                    borderRadius: 2,
                    textTransform: 'uppercase',
                  }}
                >
                  {model.badge}
                </div>

                <div style={{ marginTop: 14 }}>
                  {/* Title */}
                  <h3
                    style={{
                      margin: '0 0 16px',
                      fontFamily: FONT_DISPLAY,
                      fontSize: 26,
                      letterSpacing: '0.04em',
                      color: 'var(--gl-text)',
                      textTransform: 'uppercase',
                    }}
                  >
                    {model.name}
                  </h3>

                  {/* Description */}
                  <p
                    style={{
                      margin: '0 0 28px',
                      fontFamily: FONT_BODY,
                      fontSize: 13,
                      lineHeight: 1.6,
                      color: 'var(--gl-text-muted)',
                    }}
                  >
                    {model.desc}
                  </p>

                  {/* Features list */}
                  <ul
                    style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                    }}
                  >
                    {model.features.map(f => (
                      <li
                        key={f}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          fontFamily: FONT_BODY,
                          fontSize: 13,
                          color: 'var(--gl-text-muted)',
                        }}
                      >
                        <span style={{ color: isSelected ? 'var(--gl-accent)' : 'var(--gl-text-muted)', fontSize: 14 }}>❖</span>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Selection indicator line */}
                <motion.div
                  animate={{
                    width: isSelected ? '100%' : '0%',
                  }}
                  transition={{ duration: 0.3 }}
                  style={{
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                    height: 2,
                    background: 'var(--gl-accent)',
                  }}
                />
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

/* ==========================================================================
   8. ServicesOutcomes
   ========================================================================== */
const OUTCOMES = [
  {
    title: 'Faster Operations',
    desc: 'Reduce time spent moving information through repetitive workflows.',
    icon: 'bolt',
  },
  {
    title: 'Lower Manual Effort',
    desc: 'Automate repetitive tasks and allow teams to focus on higher-value work.',
    icon: 'settings',
  },
  {
    title: 'Better Customer Experiences',
    desc: 'Deliver faster, more consistent and more intelligent interactions across channels.',
    icon: 'support_agent',
  },
  {
    title: 'Multilingual Reach',
    desc: 'Support more users through voice, translation, and regional-language AI.',
    icon: 'translate',
  },
  {
    title: 'Connected Operations',
    desc: 'Bring AI into the tools and systems your organization already uses.',
    icon: 'hub',
  },
  {
    title: 'Continuous Intelligence',
    desc: 'Improve AI systems through evaluation, monitoring, and ongoing optimization.',
    icon: 'monitoring',
  },
]

export function ServicesOutcomes() {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)

  return (
    <section
      id="outcomes"
      className="gl-theme-transition"
      style={{
        position: 'relative',
        padding: '100px 20px',
        background: 'var(--gl-bg)', // Outcomes -> --gl-bg
        overflow: 'hidden',
      }}
    >
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 60, maxWidth: 640 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 30, height: 1, background: 'var(--gl-accent-ice)' }} />
            <span style={{ fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '0.24em', textTransform: 'uppercase', color: 'var(--gl-accent-bright)' }}>
              Business Outcomes
            </span>
          </div>
          <h2
            style={{
              margin: 0,
              fontFamily: FONT_DISPLAY,
              fontSize: 'clamp(36px, 5vw, 68px)',
              lineHeight: 0.95,
              letterSpacing: '0.03em',
              color: 'var(--gl-text)',
              textTransform: 'uppercase',
            }}
          >
            ENGINEERING BUSINESS IMPACT
          </h2>
        </div>

        {/* Outcomes Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 24,
          }}
        >
          {OUTCOMES.map((item, idx) => {
            const isHovered = hoveredIdx === idx
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, delay: idx * 0.08 }}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
                className="gl-theme-transition"
                style={{
                  padding: '34px 28px',
                  background: isHovered ? 'var(--gl-surface-hover)' : 'var(--gl-bg-elevated)', // Dark card base vs hover
                  border: '1px solid',
                  borderColor: isHovered ? 'var(--gl-accent)' : 'var(--gl-border)', // Active / normal border
                  clipPath: 'polygon(12px 0%, 100% 0%, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0% 100%, 0% 12px)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                  cursor: 'pointer',
                  position: 'relative',
                }}
              >
                {/* Icon & Title */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <span 
                    className="material-symbols-outlined service-icon"
                    style={{
                      fontSize: 24,
                      color: isHovered ? 'var(--gl-accent)' : 'var(--gl-accent-bright)',
                      transform: isHovered ? 'translateY(-2px) scale(1.05)' : 'none',
                      display: 'inline-block'
                    }}
                  >
                    {item.icon}
                  </span>
                  <h3
                    style={{
                      margin: 0,
                      fontFamily: FONT_DISPLAY,
                      fontSize: 22,
                      letterSpacing: '0.04em',
                      color: 'var(--gl-text)',
                      textTransform: 'uppercase',
                    }}
                  >
                    {item.title}
                  </h3>
                </div>

                {/* Description */}
                <p
                  style={{
                    margin: 0,
                    fontFamily: FONT_BODY,
                    fontSize: 13,
                    lineHeight: 1.65,
                    color: 'var(--gl-text-muted)',
                  }}
                >
                  {item.desc}
                </p>

                {/* Left glow line */}
                <motion.div
                  animate={{
                    height: isHovered ? '100%' : '0%',
                  }}
                  transition={{ duration: 0.3 }}
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: 2,
                    background: 'var(--gl-accent)',
                  }}
                />
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

/* ==========================================================================
   9. ServicesCTA
   ========================================================================== */
export function ServicesCTA() {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', scope: '' })
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name || !formData.email) return

    setStatus('submitting')
    setTimeout(() => {
      setStatus('success')
    }, 1200)
  }

  return (
    <section
      id="cta"
      className="gl-theme-transition"
      style={{
        position: 'relative',
        padding: '100px 20px',
        background: 'var(--gl-bg-elevated)', // CTA -> --gl-bg-elevated
        overflow: 'hidden',
      }}
    >
      {/* Glow highlight */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '60vw',
          height: '40vw',
          background: 'radial-gradient(circle, rgba(56,189,248,0.035) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <div style={{ maxWidth: 800, margin: '0 auto', position: 'relative', zIndex: 5 }}>
        <div
          className="gl-theme-transition"
          style={{
            padding: 'clamp(28px, 5vw, 60px)',
            background: 'var(--gl-bg)', // Inner container background -> --gl-bg
            border: '1px solid var(--gl-border)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            clipPath: 'polygon(16px 0%, 100% 0%, 100% calc(100% - 16px), calc(100% - 16px) 100%, 0% 100%, 0% 16px)',
          }}
        >
          <AnimatePresence mode="wait">
            {status !== 'success' ? (
              <motion.div
                key="form-container"
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div style={{ textAlign: 'center', marginBottom: 40 }}>
                  <span
                    style={{
                      fontFamily: FONT_MONO,
                      fontSize: 8,
                      color: 'var(--gl-accent-bright)', // Subheading -> #7DD3FC
                      letterSpacing: '0.24em',
                      textTransform: 'uppercase',
                      display: 'block',
                      marginBottom: 12,
                    }}
                  >
                    START YOUR AI JOURNEY
                  </span>
                  <h2
                    style={{
                      margin: '0 0 12px',
                      fontFamily: FONT_DISPLAY,
                      fontSize: 'clamp(32px, 4vw, 48px)',
                      color: 'var(--gl-text)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em',
                      lineHeight: 1.1,
                    }}
                  >
                    TURN YOUR WORKFLOW INTO AN INTELLIGENT SYSTEM
                  </h2>
                  <p
                    style={{
                      margin: 0,
                      fontFamily: FONT_BODY,
                      fontSize: 14,
                      lineHeight: 1.6,
                      color: 'var(--gl-text-muted)',
                    }}
                  >
                    Tell us about the problem, workflow, or opportunity you want to transform. We'll help identify the right AI approach and a practical path to implementation.
                  </p>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                      gap: 20,
                    }}
                  >
                    {/* Name */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ fontFamily: FONT_MONO, fontSize: 8, color: 'var(--gl-text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        Your Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        className="gl-theme-transition"
                        style={{
                          background: 'var(--gl-surface-hover)',
                          border: '1px solid var(--gl-border)',
                          color: 'var(--gl-text)',
                          fontFamily: FONT_BODY,
                          fontSize: 13,
                          padding: '12px 16px',
                          outline: 'none',
                          borderRadius: 2,
                          transition: 'border-color 0.2s',
                        }}
                        onFocus={e => (e.target.style.borderColor = 'var(--gl-accent)')}
                        onBlur={e => (e.target.style.borderColor = 'var(--gl-border)')}
                      />
                    </div>

                    {/* Email */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ fontFamily: FONT_MONO, fontSize: 8, color: 'var(--gl-text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        Work Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                        className="gl-theme-transition"
                        style={{
                          background: 'var(--gl-surface-hover)',
                          border: '1px solid var(--gl-border)',
                          color: 'var(--gl-text)',
                          fontFamily: FONT_BODY,
                          fontSize: 13,
                          padding: '12px 16px',
                          outline: 'none',
                          borderRadius: 2,
                          transition: 'border-color 0.2s',
                        }}
                        onFocus={e => (e.target.style.borderColor = 'var(--gl-accent)')}
                        onBlur={e => (e.target.style.borderColor = 'var(--gl-border)')}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                      gap: 20,
                    }}
                  >
                    {/* Company */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ fontFamily: FONT_MONO, fontSize: 8, color: 'var(--gl-text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        Company / Organization
                      </label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={e => setFormData({ ...formData, company: e.target.value })}
                        className="gl-theme-transition"
                        style={{
                          background: 'var(--gl-surface-hover)',
                          border: '1px solid var(--gl-border)',
                          color: 'var(--gl-text)',
                          fontFamily: FONT_BODY,
                          fontSize: 13,
                          padding: '12px 16px',
                          outline: 'none',
                          borderRadius: 2,
                          transition: 'border-color 0.2s',
                        }}
                        onFocus={e => (e.target.style.borderColor = 'var(--gl-accent)')}
                        onBlur={e => (e.target.style.borderColor = 'var(--gl-border)')}
                      />
                    </div>

                    {/* Scope */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ fontFamily: FONT_MONO, fontSize: 8, color: 'var(--gl-text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        Focus Area
                      </label>
                      <select
                        value={formData.scope}
                        onChange={e => setFormData({ ...formData, scope: e.target.value })}
                        className="gl-theme-transition"
                        style={{
                          background: 'var(--gl-surface-hover)',
                          border: '1px solid var(--gl-border)',
                          color: 'var(--gl-text)',
                          fontFamily: FONT_BODY,
                          fontSize: 13,
                          padding: '12px 16px',
                          outline: 'none',
                          borderRadius: 2,
                          cursor: 'pointer',
                          transition: 'border-color 0.2s',
                        }}
                        onFocus={e => (e.target.style.borderColor = 'var(--gl-accent)')}
                        onBlur={e => (e.target.style.borderColor = 'var(--gl-border)')}
                      >
                        <option value="" disabled style={{ background: 'var(--gl-surface)', color: 'var(--gl-text)' }}>Select focus...</option>
                        <option value="voice" style={{ background: 'var(--gl-surface)', color: 'var(--gl-text)' }}>Voice &amp; Conversational Systems</option>
                        <option value="translation" style={{ background: 'var(--gl-surface)', color: 'var(--gl-text)' }}>Language Systems</option>
                        <option value="workflows" style={{ background: 'var(--gl-surface)', color: 'var(--gl-text)' }}>AI Agents &amp; Automation</option>
                        <option value="rag" style={{ background: 'var(--gl-surface)', color: 'var(--gl-text)' }}>Knowledge &amp; AI Search</option>
                      </select>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="gl-theme-transition"
                    style={{
                      marginTop: 10,
                      padding: '16px 0',
                      background: '#F8FAFC', // Primary CTA background -> #F8FAFC
                      color: '#070B10', // Primary CTA text color -> #070B10
                      border: 'none',
                      fontFamily: FONT_MONO,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      clipPath: 'polygon(12px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)',
                      transition: 'transform 0.2s, opacity 0.2s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.015)')}
                    onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                  >
                    {status === 'submitting' ? 'PRODUCING BLUEPRINT...' : 'START AN AI PROJECT'}
                  </button>
                </form>
              </motion.div>
            ) : (
              <motion.div
                key="success-container"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                style={{ textAlign: 'center', padding: '40px 0' }}
              >
                <div style={{ marginBottom: 20 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 50, color: 'var(--gl-accent)' }}>
                    celebration
                  </span>
                </div>
                <h3
                  style={{
                    margin: '0 0 12px',
                    fontFamily: FONT_DISPLAY,
                    fontSize: 32,
                    color: 'var(--gl-text)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  Architecture Request Submitted
                </h3>
                <p
                  style={{
                    margin: 0,
                    fontFamily: FONT_BODY,
                    fontSize: 14,
                    lineHeight: 1.6,
                    color: 'var(--gl-accent-ice)',
                    maxWidth: 500,
                    marginInline: 'auto',
                  }}
                >
                  Our neural engineering team has received your request. We will review your scope and get in touch within 24 hours to schedule a session.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  )
}
